import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { isLoggedIn, getUser } from '@/lib/auth';
import { LogoMark } from '@/components/Logo';
import { CarIcon, BoxIcon, PawIcon } from '@/components/icons';
import { InstallAppButton } from '@/components/InstallAppButton';
import ShareSection, { BUSINESS_EMAIL } from '@/components/ShareSection';
import Testimonials from '@/components/Testimonials';
import LegalLinks from '@/components/LegalLinks';
import { PriceTag } from '@/components/PlanCard';
import { useSiteConfig, fillTokens } from '@/lib/siteConfig';
import { useFoundingOffer } from '@/lib/offer';
import OfferBanner from '@/components/OfferBanner';
import { useSeo } from '@/lib/seo';
import LeadCapture from '@/components/LeadCapture';
import DemoVideo, { DEMO_VIDEO_LD } from '@/components/DemoVideo';

const ORG_LD = {
  '@context': 'https://schema.org',
  '@graph': [
    { '@type': 'Organization', name: 'DotLink', url: 'https://dotlinkapp.com', logo: 'https://dotlinkapp.com/logo192.png', email: 'business@dotlinkapp.com' },
    { '@type': 'WebSite', name: 'DotLink', url: 'https://dotlinkapp.com' },
  ],
};

const LandingPage = () => {
  const navigate = useNavigate();
  const loggedIn = isLoggedIn();
  const home = getUser()?.is_partner ? '/partner' : '/dashboard';
  const { config } = useSiteConfig();
  const pricing = config.pricing;
  const offer = useFoundingOffer();
  useSeo({
    title: 'DotLink | QR Tags for Cars, Pets & Valuables',
    description: 'Put a DotLink QR on your car, bag or pet. Anyone who finds it can call or message you, and your phone number stays private.',
    path: '/',
    image: 'https://dotlinkapp.com/promo/og-home.jpg',
    jsonLd: [DEMO_VIDEO_LD, ORG_LD],
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      {/* Top Nav */}
      <div className="container mx-auto px-4 pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark size="sm" />
            <span className="text-lg font-bold text-gray-800">DotLink</span>
          </div>
          <div className="flex items-center gap-3">
            <InstallAppButton />
            {config.partner_plan?.enabled !== false && (
              <Link to="/business" className="hidden sm:inline-flex items-center gap-1 rounded-full bg-indigo-900 text-white text-sm font-semibold px-4 py-1.5 hover:bg-indigo-800">For business</Link>
            )}
            <button
              onClick={() => navigate(loggedIn ? home : '/login')}
              className="text-indigo-600 font-semibold text-sm hover:underline"
            >
              {loggedIn ? (home === '/partner' ? 'Partner portal →' : 'Go to Dashboard →') : 'Log In'}
            </button>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-6">
            <LogoMark size="lg" />
            <p className="text-5xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              DotLink
            </p>
          </div>

          <h1 className="text-2xl text-gray-700 mb-4 font-semibold">
            QR Tags for Your Car, Pets &amp; Valuables
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-8">
            Connect, Call, Communicate - Stay connected with your valuable items through smart QR codes
          </p>
          
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => navigate(loggedIn ? home : '/register')}
              className="btn-press bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:-translate-y-1 transition-all"
            >
              Get Started
            </button>
            <button
              onClick={() => window.scrollTo({ top: document.getElementById('features').offsetTop, behavior: 'smooth' })}
              className="btn-press bg-white text-indigo-600 px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:-translate-y-1 transition-all border-2 border-indigo-600"
            >
              Learn More
            </button>
            {config.partner_plan?.enabled !== false && (
              <Link to="/business" className="btn-press bg-indigo-900 text-white px-8 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:-translate-y-1 transition-all">
                Business plan · ₹{config.partner_plan?.price_per_qr_inr ?? 3}/QR
              </Link>
            )}
          </div>
        </div>

        <DemoVideo className="mb-16" />

        {/* Features Grid */}
        <div id="features" className="grid md:grid-cols-3 gap-8 mb-16">
          <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-2xl mb-4">
              <CarIcon />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Vehicles</h3>
            <p className="text-gray-600">
              Generate QR codes for your vehicles. Lost car? Finder can call you instantly with WebRTC calling.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-2xl mb-4">
              <BoxIcon />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Assets</h3>
            <p className="text-gray-600">
              Protect your valuable items with smart QR codes. Get real-time notifications when scanned.
            </p>
          </div>

          <div className="bg-white rounded-3xl p-8 shadow-xl hover:shadow-2xl hover:-translate-y-2 transition-all">
            <div className="w-12 h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-2xl mb-4">
              <PawIcon />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-3">Pets</h3>
            <p className="text-gray-600">
              Keep your pets safe with QR tags. Finder can video call you to show your pet instantly.
            </p>
          </div>
        </div>

        {/* Key Features */}
        <div className="bg-white rounded-3xl p-12 shadow-xl mb-16">
          <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
            🌟 Amazing Features
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {config.features.map((f) => (
              <div key={f.title} className="flex gap-4">
                <div className="text-3xl">{f.icon}</div>
                <div>
                  <h4 className="text-xl font-semibold text-gray-800 mb-2">{f.title}</h4>
                  <p className="text-gray-600">{fillTokens(f.text, pricing)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Plans Comparison */}
        <div className="bg-gradient-to-br from-indigo-600 to-purple-600 rounded-3xl p-12 text-white mb-16">
          <h2 className="text-3xl font-bold mb-6 text-center">
            Choose Your Plan
          </h2>
          <OfferBanner offer={offer} className="max-w-3xl mx-auto mb-8" />
          
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {/* Free Plan */}
            <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 border border-white/20">
              <div className="text-4xl mb-3">🎁</div>
              <h3 className="text-2xl font-bold mb-2">Free</h3>
              <p className="text-3xl font-bold mb-6">₹0 <span className="text-lg font-normal">/forever</span></p>
              <ul className="space-y-3 mb-6">
                {pricing.free_features.map((f) => (
                  <li key={f.text} className={`flex items-center gap-2 ${f.included ? '' : 'opacity-60'}`}>
                    <span>{f.included ? '✓' : '✕'}</span> {f.text}
                  </li>
                ))}
              </ul>
            </div>

            {/* PRO Plan */}
            <div className="bg-white text-indigo-600 rounded-2xl p-8 shadow-2xl transform scale-105">
              <div className="text-4xl mb-3">👑</div>
              <h3 className="text-2xl font-bold mb-2">PRO</h3>
              <PriceTag pricing={pricing} offer={offer} className="text-3xl font-bold mb-6" />
              <ul className="space-y-3 mb-6">
                {pricing.pro_features.map((f) => (
                  <li key={f.text} className={`flex items-center gap-2 ${f.included ? '' : 'opacity-60'}`}>
                    <span>{f.included ? '✓' : '✕'}</span> {f.text}
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/plans')}
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-3 rounded-full font-semibold hover:shadow-xl transition-all"
              >
                Get PRO
              </button>
            </div>
          </div>
        </div>

        {config.partner_plan?.enabled !== false && (
          <div className="rounded-3xl bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 text-white p-8 sm:p-12 mb-16 shadow-2xl">
            <div className="flex flex-wrap items-center justify-between gap-8 max-w-4xl mx-auto">
              <div className="max-w-md">
                <p className="text-xs font-bold tracking-widest text-indigo-200">NEW · PARTNER PLAN FOR BUSINESSES</p>
                <h2 className="text-3xl font-extrabold mt-2">Your brand on every QR</h2>
                <p className="text-indigo-100 mt-3">Dealerships, schools, societies, pet shops and fleets: customised QR stickers and a scan page with your own branding. Each customer activates their own QR. Pay monthly for the QR codes you need.</p>
              </div>
              <div className="text-center">
                <p className="text-5xl font-extrabold">₹{config.partner_plan?.price_per_qr_inr ?? 3}</p>
                <p className="text-sm text-indigo-200">per QR / month</p>
                <p className="text-xs text-indigo-200 mb-4">minimum {config.partner_plan?.min_qr ?? 50} QR codes</p>
                <Link to="/business" className="inline-block rounded-full bg-white text-indigo-900 font-bold px-8 py-3 shadow-lg">See partner plan</Link>
              </div>
            </div>
          </div>
        )}

        <Testimonials className="mb-16" />

        <ShareSection />

        {/* CTA */}
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-800 mb-4">
            Ready to Get Started?
          </h2>
          <p className="text-lg text-gray-600 mb-8">
            Protect your car, bag or pet in a couple of minutes — your first vehicle is free
          </p>
          <button
            onClick={() => navigate(loggedIn ? home : '/register')}
            className="btn-press bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-12 py-4 rounded-full font-semibold text-lg hover:shadow-2xl hover:-translate-y-1 transition-all"
          >
            Start Free Today
          </button>
        </div>
      </div>

      <div className="container mx-auto px-4 max-w-3xl"><LeadCapture interest="car" source="/" /></div>

      {/* Footer */}
      <footer className="bg-white/50 backdrop-blur-xl py-8 mt-16">
        <div className="container mx-auto px-4 text-center text-gray-600">
          <nav className="flex flex-wrap justify-center gap-x-5 gap-y-1 text-sm mb-3" aria-label="Footer">
            <Link to="/car" className="hover:text-indigo-600">Car</Link>
            <Link to="/pet" className="hover:text-indigo-600">Pet</Link>
            <Link to="/bike" className="hover:text-indigo-600">Bike</Link>
            <Link to="/assets" className="hover:text-indigo-600">Bags &amp; valuables</Link>
            <Link to="/society" className="hover:text-indigo-600">Society</Link>
            <Link to="/partners" className="hover:text-indigo-600">Partners</Link>
            <Link to="/partners/vets" className="hover:text-indigo-600">For vets</Link>
            <Link to="/partners/car-wash" className="hover:text-indigo-600">For car washes</Link>
            <Link to="/business" className="hover:text-indigo-600">For business</Link>
            <Link to="/blog" className="hover:text-indigo-600">Guides</Link>
            <Link to="/demo" className="hover:text-indigo-600">Live demo</Link>
          </nav>
          <p className="mb-2">🔗 DotLink - Connect, Call, Communicate</p>
          <LegalLinks className="mb-3" />
          <p className="mb-2 text-sm">
            ✉️ <a href={`mailto:${BUSINESS_EMAIL}`} className="font-semibold text-indigo-600 hover:underline">{BUSINESS_EMAIL}</a>
          </p>
          <p className="text-sm text-gray-500">© 2026 DotLink. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
